import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import {
  selectedClientSelector,
  setSelectedClient,
  clearSelectedClient
} from '../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../store/slices/clients';
import { authSelector } from '../../store/slices/auth';
import {
  SEO_AUDIT_BASE,
  API_KEY,
  API_CLIENTS_NAME
} from '../../utils/constants';
import ReactSelect from 'react-select';
import {
  Search,
  Loader2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  Download,
  Sparkles,
  Monitor,
  Globe,
  Shield,
  Zap,
  Info,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const CACHE_PREFIX = 'seo_audit_v3_cache_';

const getCacheKeyForUrl = (url) =>
  CACHE_PREFIX +
  (url || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
    .replace(/[^a-zA-Z0-9]/g, '_');

const PILLARS = [
  {
    id: 'onPage',
    title: 'On-Page & Content',
    icon: Shield,
    color: 'text-green-600',
    checks: [
      'SEO Title',
      'SEO Description',
      'Common Keywords',
      'Keywords in Title & Description',
      'H1 Heading',
      'H2 Headings',
      'Image ALT Attributes',
      'Links Ratio',
      'Duplicate Content'
    ]
  },
  {
    id: 'technical',
    title: 'Technical & Crawlability',
    icon: Monitor,
    color: 'text-blue-600',
    checks: [
      'HTTPS / SSL',
      'Canonical URL',
      'Open Graph Tags',
      'Structured Data (JSON-LD)',
      'Robots.txt',
      'Sitemap.xml',
      'Broken Internal Links',
      'Broken Internal Pages',
      'Redirect Loops'
    ]
  },
  {
    id: 'performance',
    title: 'Performance & UX',
    icon: Zap,
    color: 'text-amber-600',
    checks: ['Mobile Friendly', 'Page Speed']
  },
  {
    id: 'authority',
    title: 'Authority & Trust',
    icon: Globe,
    color: 'text-purple-600',
    checks: ['Low Domain Authority', 'High Spam Score']
  }
];

const STATUS_CONFIG = {
  Passed: {
    badge: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
    Icon: CheckCircle
  },
  'Needs Work': {
    badge:
      'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    Icon: AlertTriangle
  },
  Failed: {
    badge: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
    Icon: XCircle
  }
};

const SEVERITY_MAP = {
  critical: 'Failed',
  high: 'Failed',
  medium: 'Needs Work',
  low: 'Needs Work',
  info: 'Passed'
};

const getScoreColor = (score) => {
  if (score >= 80) return '#22c55e';
  if (score >= 50) return '#eab308';
  return '#ef4444';
};

const WebsiteSEOAuditV3 = () => {
  const dispatch = useDispatch();
  const { user } = useUser();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);

  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const [customUrl, setCustomUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  const [scanMessage, setScanMessage] = useState('');
  const [auditResults, setAuditResults] = useState(null);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    onPage: true,
    technical: true,
    performance: false,
    authority: false,
    other: false
  });
  const [expandedIndicator, setExpandedIndicator] = useState(null);
  const [expandedOtherIndicator, setExpandedOtherIndicator] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const reportRef = useRef(null);
  const websiteUrl = selectedClient?.mergeFields?.['~Website'] || '';

  // --- Auto-lookup debounced effect ---
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customUrl && customUrl.length > 3 && !isScanning && !auditResults) {
        startAudit(customUrl, false, true);
      }
    }, 1200); // 1.2s debounce
    return () => clearTimeout(timer);
  }, [customUrl]);

  useEffect(() => {
    if (websiteUrl) {
      setCustomUrl(websiteUrl);
      startAudit(websiteUrl, false, true);
    }
  }, [websiteUrl]);

  // --- Cache helpers ---
  const saveCachedResults = (url, results) => {
    if (!url || !results) return;
    try {
      const cleanUrl = url
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/$/, '');
      localStorage.setItem(
        getCacheKeyForUrl(cleanUrl),
        JSON.stringify({ results, savedAt: new Date().toISOString() })
      );
    } catch {}
  };

  const loadCachedResults = (url) => {
    if (!url) return null;
    try {
      const cleanUrl = url
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/$/, '');
      const raw = localStorage.getItem(getCacheKeyForUrl(cleanUrl));
      if (!raw) return null;
      const { results, savedAt } = JSON.parse(raw);
      if (Date.now() - new Date(savedAt).getTime() > 24 * 60 * 60 * 1000)
        return null;
      return results;
    } catch {
      return null;
    }
  };

  const clearCachedResults = (url) => {
    if (!url) return;
    const cleanUrl = url
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');
    localStorage.removeItem(getCacheKeyForUrl(cleanUrl));
  };

  const getCustomerList = () => {
    if (!customerList?.length) dispatch(getClients());
    return (customerList || []).map((c) => ({
      ...c,
      label: c.clientName,
      value: c.clientName
    }));
  };

  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;
    dispatch(clearSelectedClient());
    let clientData = selectedOption;
    try {
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );
      clientData = response?.data || selectedOption;
    } catch {}
    dispatch(setSelectedClient(clientData));
    const newUrl = clientData?.mergeFields?.['~Website'] || '';
    setAuditResults(null);
    if (newUrl) {
      setCustomUrl(newUrl);
      startAudit(newUrl, false, true);
    }
  };

  const startAudit = async (url, forceRescan = false, lookupOnly = false) => {
    let targetUrl = (url || customUrl || '').trim();
    if (!targetUrl) return;

    const cleanCacheKey = targetUrl
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');

    if (!forceRescan) {
      const cached = loadCachedResults(cleanCacheKey);
      if (cached) {
        setAuditResults(cached);
        setError('');
        return;
      }
    } else {
      clearCachedResults(cleanCacheKey);
    }

    if (lookupOnly) setIsLookupLoading(true);
    else setIsScanning(true);

    setError('');
    if (!lookupOnly) {
      setAuditResults(null);
      setScanMessage('Initiating SEO Health Check...');
    }

    try {
      const res = await fetch(SEO_AUDIT_BASE + '/audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: targetUrl,
          maxPages: 50,
          version: 'v3',
          forceRescan,
          lookupOnly
        })
      });
      const startData = await res.json();
      if (!res.ok) {
        if (lookupOnly) return;
        throw new Error(startData.message || 'Failed to start audit');
      }

      if (startData.status === 'completed') {
        setAuditResults(startData);
        saveCachedResults(cleanCacheKey, startData);
        setIsScanning(false);
        setIsLookupLoading(false);
        return;
      }

      if (lookupOnly) {
        setIsLookupLoading(false);
        return;
      }

      const reportId = startData.reportId;
      let completed = false;
      let attempts = 0;
      const maxAttempts = 500;
      while (!completed && attempts < maxAttempts) {
        attempts++;
        await new Promise((r) => setTimeout(r, 5000));
        const statusRes = await fetch(
          `${SEO_AUDIT_BASE}/audit-v2/job/${reportId}`
        );
        const statusData = await statusRes.json();
        if (statusData.status === 'completed') {
          setAuditResults(statusData);
          saveCachedResults(cleanCacheKey, statusData);
          completed = true;
        } else if (statusData.status === 'failed') {
          throw new Error(statusData.message || 'Audit failed');
        } else {
          setScanMessage(
            `${
              statusData.message || 'Analyzing...'
            } (Attempt ${attempts}/${maxAttempts})`
          );
        }
      }
      if (!completed) throw new Error('Audit timed out');
    } catch (err) {
      if (!lookupOnly) setError(err.message);
    } finally {
      setIsScanning(false);
      setIsLookupLoading(false);
      setScanMessage('');
    }
  };

  const handleDownloadReport = async () => {
    if (!auditResults) return;
    try {
      setIsPrinting(true);
      const originalState = { ...expandedSections };
      setExpandedSections({
        technical: true,
        onPage: true,
        performance: true,
        authority: true,
        other: true
      });

      // Wait for re-render and expansion
      await new Promise((r) => setTimeout(r, 1500));

      const input = reportRef.current;
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#FAFAFA',
        windowWidth: 1200
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = pdfHeight;
      let position = 0;
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Add first page
      pdf.addImage(
        imgData,
        'PNG',
        0,
        position,
        pdfWidth,
        pdfHeight,
        undefined,
        'FAST'
      );
      heightLeft -= pageHeight;

      // Add subsequent pages if content is longer than one page
      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          'PNG',
          0,
          position,
          pdfWidth,
          pdfHeight,
          undefined,
          'FAST'
        );
        heightLeft -= pageHeight;
      }

      pdf.save(`SEO-Audit-${auditResults.target}.pdf`);

      setIsPrinting(false);
      setExpandedSections(originalState);
    } catch (err) {
      console.error('PDF Generation Error:', err);
      setIsPrinting(false);
    }
  };

  const toggleSection = (id) =>
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleIndicator = (id) =>
    setExpandedIndicator(expandedIndicator === id ? null : id);
  const toggleOtherIndicator = (id) =>
    setExpandedOtherIndicator(expandedOtherIndicator === id ? null : id);

  const scoreRadius = 52;
  const circumference = 2 * Math.PI * scoreRadius;
  const score = auditResults?.overallScore ?? 0;
  const strokeOffset = circumference - (score / 100) * circumference;

  /**
   * Helper to format Performance Section specific data
   */
  const getPerformanceMetricDisplay = (checkTitle, psiData) => {
    if (!psiData || !psiData.coreWebVitals) {
      console.log(`[V3 Audit] PSI data missing for ${checkTitle}`, psiData);
      return 'Optimized correctly.';
    }

    if (checkTitle === 'Page Speed') {
      const lcp = psiData.coreWebVitals.lcp || 'N/A';
      const cls = psiData.coreWebVitals.cls || 'N/A';
      console.log(`[V3 Audit] Displaying Page Speed: LCP=${lcp}, CLS=${cls}`);
      return `Optimized: LCP is ${lcp}, CLS is ${cls}.`;
    }

    if (checkTitle === 'Mobile Friendly') {
      const perfScore = psiData.scores?.performance || 0;
      console.log(`[V3 Audit] Displaying Mobile Friendly: Score=${perfScore}`);
      return `Optimized: Mobile Performance Score is ${Math.round(
        perfScore
      )}/100.`;
    }

    return 'Optimized correctly.';
  };

  return (
    <main className="p-4 sm:p-6 lg:p-10 space-y-6 bg-[#FAFAFA] min-h-screen">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
            Website SEO Audit
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Professional SEO Health Assessment
          </p>
        </div>
        {isSuperAdmin && (
          <div className="w-full lg:w-72">
            <ReactSelect
              placeholder="Search Client"
              options={getCustomerList()}
              onChange={handleChangeClient}
              styles={{
                control: (p) => ({
                  ...p,
                  borderRadius: '0.75rem',
                  height: '48px'
                })
              }}
            />
          </div>
        )}
      </div>

      <Card className="rounded-2xl border shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Input
                placeholder="https://example.com"
                value={customUrl}
                onChange={(e) => {
                  setAuditResults(null);
                  setCustomUrl(e.target.value);
                }}
                className="h-12 w-full rounded-xl pr-10"
              />
              {isLookupLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            <Button
              onClick={() => startAudit(null, true)}
              disabled={isScanning || !customUrl}
              className="h-12 px-8 rounded-xl bg-[#3767A9] text-white">
              {isScanning ? (
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              {isScanning ? 'Scanning...' : 'Run New Audit'}
            </Button>
          </div>
          {error && (
            <p className="text-red-500 mt-2 text-sm font-semibold flex items-center gap-1">
              <AlertCircle className="h-4 w-4" /> {error}
            </p>
          )}
          {isScanning && (
            <div className="mt-3 flex items-center gap-2 text-blue-600 text-sm animate-pulse">
              <Loader2 className="h-4 w-4 animate-spin" /> {scanMessage}
            </div>
          )}
        </CardContent>
      </Card>

      {auditResults && (
        <div
          ref={reportRef}
          className="space-y-6 animate-in fade-in duration-500">
          <Card className="rounded-2xl border-0 shadow-lg bg-white overflow-hidden">
            <div className="h-2 bg-primary" />
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="relative h-32 w-32 shrink-0">
                  <svg
                    className="w-full h-full -rotate-90"
                    viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r={scoreRadius}
                      className="fill-none stroke-gray-100"
                      strokeWidth="12"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={scoreRadius}
                      className="fill-none transition-all duration-1000"
                      stroke={getScoreColor(score)}
                      strokeWidth="12"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeOffset}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-black">{score}</span>
                    <span className="text-[10px] text-muted-foreground uppercase font-bold">
                      Health Score
                    </span>
                  </div>
                </div>
                <div className="flex-1 space-y-4 text-center md:text-left">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground truncate">
                      {auditResults.target}
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Report generated on{' '}
                      {new Date(
                        auditResults.createdAt || Date.now()
                      ).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center md:justify-start gap-4">
                    <div className="px-4 py-2 rounded-xl bg-muted/50 border border-border">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase">
                        Crawl Depth
                      </p>
                      <p className="text-lg font-bold">
                        {auditResults.crawlInfo?.pagesCrawled || 0} Pages
                      </p>
                    </div>
                    <div className="px-4 py-2 rounded-xl bg-muted/50 border border-border">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase">
                        Domain Authority
                      </p>
                      <p className="text-lg font-bold text-purple-600">
                        {auditResults.moz?.domain_authority || 'N/A'}
                      </p>
                    </div>
                    <div className="px-4 py-2 rounded-xl bg-muted/50 border border-border">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase">
                        Spam Score
                      </p>
                      <p className="text-lg font-bold text-orange-600">
                        {auditResults.moz?.spam_score || 0}%
                      </p>
                    </div>
                  </div>
                </div>
                {!isPrinting && (
                  <Button
                    onClick={handleDownloadReport}
                    variant="outline"
                    className="rounded-xl h-12 gap-2">
                    <Download className="h-4 w-4" /> Export PDF
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PILLARS.map((p) => {
              const pillarScore = auditResults.scores?.[p.id] ?? 0;
              return (
                <Card key={p.id} className="rounded-2xl border shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`p-2 rounded-lg bg-muted/50 ${p.color}`}>
                        <p.icon className="h-5 w-5" />
                      </div>
                    </div>
                    <h4 className="text-xs font-bold text-muted-foreground uppercase mb-1">
                      {p.title}
                    </h4>
                    <div className="flex items-end justify-between">
                      <span className="text-2xl font-black">
                        {pillarScore}%
                      </span>
                      <div className="h-1 w-24 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary"
                          style={{
                            width: `${pillarScore}%`,
                            backgroundColor: getScoreColor(pillarScore)
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="space-y-4">
            {PILLARS.map((pillar) => {
              const pillarIssues = (
                auditResults.indicators ||
                auditResults.issues ||
                []
              ).filter((i) => i.category === pillar.id);
              const isOpen = expandedSections[pillar.id];
              return (
                <Card
                  key={pillar.id}
                  className="rounded-2xl border shadow-sm overflow-hidden">
                  <button
                    onClick={() => toggleSection(pillar.id)}
                    className="w-full flex items-center justify-between px-6 py-4 bg-muted/40 hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-3">
                      <pillar.icon className={`h-4 w-4 ${pillar.color}`} />
                      <h2 className="text-sm font-bold uppercase tracking-wider">
                        {pillar.title}
                      </h2>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold">
                        Score: {auditResults.scores?.[pillar.id]}%
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-border bg-white">
                      {/* 1. Static Checklist */}
                      {pillar.checks.map((checkTitle, idx) => {
                        const issue = pillarIssues.find(
                          (i) => i.title === checkTitle
                        );
                        const status = issue
                          ? SEVERITY_MAP[issue.severity] || 'Needs Work'
                          : 'Passed';
                        const config = STATUS_CONFIG[status];
                        const StatusIcon = config.Icon;
                        const isIndicatorOpen =
                          isPrinting ||
                          expandedIndicator === `${pillar.id}-${idx}`;
                        return (
                          <div key={idx}>
                            <button
                              onClick={() =>
                                issue
                                  ? toggleIndicator(`${pillar.id}-${idx}`)
                                  : null
                              }
                              className={`w-full flex items-center gap-4 px-6 py-4 transition-colors text-left ${
                                issue
                                  ? 'hover:bg-muted/30 cursor-pointer'
                                  : 'cursor-default'
                              }`}>
                              <div className="w-1/3 shrink-0">
                                <h3 className="font-bold text-foreground text-sm">
                                  {checkTitle}
                                </h3>
                              </div>
                              <div className="flex-1 hidden md:block">
                                <p className="text-sm text-muted-foreground truncate">
                                  {issue
                                    ? issue.description
                                    : pillar.id === 'performance'
                                    ? getPerformanceMetricDisplay(
                                        checkTitle,
                                        auditResults.psi?.mobile
                                      )
                                    : 'Optimized correctly.'}
                                </p>
                              </div>
                              <Badge
                                className={`${config.badge} text-xs px-2.5 py-1 shrink-0 border gap-1 font-bold rounded-full`}>
                                <StatusIcon className="h-3 w-3" />
                                {status}
                              </Badge>
                              {issue && (
                                <ChevronDown
                                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                                    isIndicatorOpen ? 'rotate-180' : ''
                                  }`}
                                />
                              )}
                            </button>
                            {isIndicatorOpen && issue && (
                              <div className="px-6 pb-6 pt-2 space-y-4">
                                <p className="text-sm text-muted-foreground">
                                  {issue.description}
                                </p>
                                {issue.howToFix && (
                                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                                    <h5 className="text-xs font-black text-green-800 uppercase tracking-widest mb-2 flex items-center gap-2">
                                      <Sparkles className="h-3.5 w-3.5" />{' '}
                                      Recommended Fix
                                    </h5>
                                    <p className="text-sm text-green-700 leading-relaxed font-medium">
                                      {issue.howToFix}
                                    </p>
                                  </div>
                                )}
                                {issue.items?.length > 0 && (
                                  <div className="rounded-xl border border-border bg-muted/20 p-4">
                                    <h5 className="text-xs font-bold text-muted-foreground uppercase mb-3">
                                      Affected Pages / Resources
                                    </h5>
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                      {issue.items.map((item, i) => (
                                        <div
                                          key={i}
                                          className="flex items-center gap-2 text-xs">
                                          <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                          <a
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline truncate">
                                            {item.url}
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* 2. Extra API issues */}
                      {pillarIssues
                        .filter((i) => !pillar.checks.includes(i.title))
                        .map((issue, idx) => {
                          const status =
                            SEVERITY_MAP[issue.severity] || 'Needs Work';
                          const config = STATUS_CONFIG[status];
                          const StatusIcon = config.Icon;
                          const globalIdx = pillar.checks.length + idx;
                          const isIndicatorOpen =
                            isPrinting ||
                            expandedIndicator === `${pillar.id}-${globalIdx}`;
                          return (
                            <div key={`dyn-${idx}`}>
                              <button
                                onClick={() =>
                                  toggleIndicator(`${pillar.id}-${globalIdx}`)
                                }
                                className="w-full flex items-center gap-4 px-6 py-4 hover:bg-muted/30 transition-colors text-left cursor-pointer">
                                <div className="w-1/3 shrink-0">
                                  <h3 className="font-bold text-foreground text-sm">
                                    {issue.title}
                                  </h3>
                                </div>
                                <div className="flex-1 hidden md:block">
                                  <p className="text-sm text-muted-foreground truncate">
                                    {issue.description}
                                  </p>
                                </div>
                                <Badge
                                  className={`${config.badge} text-xs px-2.5 py-1 shrink-0 border gap-1 font-bold rounded-full`}>
                                  <StatusIcon className="h-3 w-3" />
                                  {status}
                                </Badge>
                                <ChevronDown
                                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                                    isIndicatorOpen ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>
                              {isIndicatorOpen && (
                                <div className="px-6 pb-6 pt-2 space-y-4">
                                  <p className="text-sm text-muted-foreground">
                                    {issue.description}
                                  </p>
                                  {issue.howToFix && (
                                    <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                                      <h5 className="text-xs font-black text-green-800 uppercase tracking-widest mb-2 flex items-center gap-2">
                                        <Sparkles className="h-3.5 w-3.5" />{' '}
                                        Recommended Fix
                                      </h5>
                                      <p className="text-sm text-green-700 leading-relaxed font-medium">
                                        {issue.howToFix}
                                      </p>
                                    </div>
                                  )}
                                  {issue.items?.length > 0 && (
                                    <div className="rounded-xl border border-border bg-muted/20 p-4">
                                      <h5 className="text-xs font-bold text-muted-foreground uppercase mb-3">
                                        Affected Pages
                                      </h5>
                                      <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {issue.items.map((item, i) => (
                                          <div
                                            key={i}
                                            className="flex items-center gap-2 text-xs">
                                            <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                            <a
                                              href={item.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-primary hover:underline truncate">
                                              {item.url}
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {auditResults.otherIssues && auditResults.otherIssues.length > 0 && (
            <Card className="rounded-2xl border shadow-sm overflow-hidden">
              <button
                onClick={() => toggleSection('other')}
                className="w-full flex items-center justify-between px-6 py-4 bg-amber-50/50 hover:bg-amber-100/50 transition-colors">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <h2 className="text-sm font-bold uppercase tracking-wider text-amber-900">
                    Other Technical Warnings
                  </h2>
                  <Badge
                    variant="outline"
                    className="bg-white text-amber-700 border-amber-200 ml-2">
                    {auditResults.otherIssues.length} items
                  </Badge>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-amber-600 transition-transform duration-200 ${
                    expandedSections.other ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedSections.other && (
                <div className="divide-y divide-amber-100 bg-white">
                  {auditResults.otherIssues.map((issue, idx) => {
                    const isOtherOpen =
                      isPrinting || expandedOtherIndicator === idx;
                    return (
                      <div key={idx}>
                        <button
                          onClick={() => toggleOtherIndicator(idx)}
                          className="w-full p-4 px-6 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:bg-amber-50/30 transition-colors text-left">
                          <div className="flex items-start gap-3">
                            <div className="h-2 w-2 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                            <div>
                              <p className="text-sm font-bold text-foreground">
                                {issue.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {issue.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <ChevronDown
                              className={`h-4 w-4 text-amber-600 transition-transform duration-200 ${
                                isOtherOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </button>
                        {isOtherOpen && (
                          <div className="px-11 pb-5 space-y-4">
                            <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                              <p className="text-[10px] font-black text-amber-700 uppercase mb-1">
                                Suggested Fix
                              </p>
                              <p className="text-xs text-amber-600 leading-relaxed font-medium">
                                {issue.howToFix}
                              </p>
                            </div>

                            {issue.items?.length > 0 && (
                              <div className="rounded-xl border border-border bg-white p-3">
                                <h5 className="text-[10px] font-bold text-amber-700 uppercase mb-2">
                                  Affected Pages
                                </h5>
                                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                  {issue.items.map((item, i) => (
                                    <div
                                      key={i}
                                      className="flex items-center gap-2 text-[11px]">
                                      <div className="h-1 w-1 rounded-full bg-amber-400 shrink-0" />
                                      <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-amber-800 hover:underline truncate">
                                        {item.url}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </main>
  );
};

export default WebsiteSEOAuditV3;
